import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Activity, Loader2, X } from "lucide-react";
import { useToast } from "../ToastContext";
import EntryInjectGroups, {
  entryKey,
  firstEntryOf,
} from "./EntryInjectGroups";
import { injectTeamScenarioEntry } from "../../api/services/platformAdminService";
import { describeEntryOutcome } from "./injectOutcome";

// Select a single noise/decoy/department entry, then fire it at one team with
// the Inject button. The per-team result is surfaced as a toast.
export default function TeamNoiseModal({ run, ready, onClose }) {
  const { showToast } = useToast();
  const [selectedEntry, setSelectedEntry] = useState(() => firstEntryOf(run));
  const selectedEntryKey = selectedEntry
    ? entryKey(selectedEntry.family, selectedEntry.name)
    : null;

  useEffect(() => {
    const handleKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // firingKey is "<family>:<name>" while a fire call is in flight, else null.
  const [firingKey, setFiringKey] = useState(null);
  const fireEntry = async (family, name) => {
    if (firingKey) return;
    setFiringKey(`${family}:${name}`);
    try {
      const data = await injectTeamScenarioEntry({
        scenarioId: run.scenario_id,
        family,
        name,
        teamId: run.team_id,
      });
      const { type, message } = describeEntryOutcome(
        data?.results?.[0],
        data?.name,
      );
      showToast(message, type);
    } catch (err) {
      showToast(err?.message || "Failed to send inject.", "error");
    } finally {
      setFiringKey(null);
    }
  };

  const canInject = Boolean(selectedEntry) && ready && !firingKey;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col w-full max-w-md h-125 max-h-[85vh] rounded-xl border border-(--color-border) bg-(--color-bg) shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-(--color-border) shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-400">
              Team noise
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-(--color-text-primary) truncate">
              {run.team_name || "Unnamed Team"}
            </h2>
            <p className="mt-0.5 text-xs text-(--color-text-muted)">
              Pick a noise, decoy, or department entry, then inject it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -m-1 rounded-md text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {!ready && (
            <p className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-400">
              This team's AttackBox isn't ready yet.
            </p>
          )}
          <EntryInjectGroups
            run={run}
            selectedKey={selectedEntryKey}
            onSelect={(family, name) => setSelectedEntry({ family, name })}
          />
        </div>

        <div className="border-t border-(--color-border) p-3 shrink-0">
          <button
            type="button"
            onClick={() =>
              selectedEntry &&
              fireEntry(selectedEntry.family, selectedEntry.name)
            }
            disabled={!canInject}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-linear-to-b from-sky-300 to-sky-400 px-4 py-3.5 text-sm font-bold text-sky-950 shadow-lg shadow-sky-500/30 transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
          >
            {firingKey ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Activity className="h-4 w-4 shrink-0" />
            )}
            {firingKey ? "Injecting…" : "Inject Noise"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
